import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { usePagination } from "../hooks";
import {
  Box,
  Button,
  Typography,
  TextField,
  InputBase,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Skeleton,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { SCREEN_IDS } from "../components/FullComponent.js";
import { translateText } from "../translations/translationService";

import { typeConnect, backgroundColor2 } from "../constants/Constants";
import { INTERMEDIATE_WEIGHING_COLORS } from "../style/colors.js";
import {
  getFullMo,
  insertNewIntermediateWeight,
  setLog,
} from "../services/ConnectDataBase";
import { getCurrentDateTime, formatDate } from "../services/Functions";
import { updateListaPesos } from "../redux/reducers";
import DeleteWeightButton from "../components/Pesajes/ComponentDeleteWeightButton";
import { notify } from "../services/ui/notify";

// Función para normalizar valores numéricos desde básculas
const normalizeNumericInput = (value) => {
  if (!value) return "";
  
  // Convertir a string y limpiar
  let cleaned = String(value).trim();
  
  // Reemplazar comas por puntos (para compatibilidad con diferentes formatos)
  cleaned = cleaned.replace(/,/g, ".");
  
  // Eliminar caracteres no numéricos excepto punto y signo negativo al inicio
  cleaned = cleaned.replace(/[^\d.-]/g, "");
  
  // Si empieza con punto, agregar 0 al inicio
  if (cleaned.startsWith(".")) {
    cleaned = "0" + cleaned;
  }
  
  // Permitir solo un punto decimal
  const parts = cleaned.split(".");
  if (parts.length > 2) {
    cleaned = parts[0] + "." + parts.slice(1).join("");
  }
  
  return cleaned;
};

export default function IntermediateWeighing() {
  const [isLoading, setIsLoading] = useState(true);
  // Hook de paginación
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(0, 20);
  const scanInputRef = useRef(null);
  const dispatch = useDispatch();
  const screenName = "IntermediateWeighing";
  const currentScreenId = SCREEN_IDS.ADMIN_INTERMEDIATE_WEIGHT;
  const tipo_tiempo = "WEIGHT";
  const lang = useSelector((state) => state.lang.value);

  const listData = useSelector((state) => state.pesosList.listaPesos);
  const usuario = useSelector((state) => state.user.name);
  const equipo = useSelector((state) => state.user.team);
  const [searchedMOs, setSearchedMOs] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const authQualityUsers = useSelector((state) => state.authQualityUsers);
  const [refSearch, setRefSearch] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [weights, setWeights] = useState({});
  const [cantidadInput, setCantidadInput] = useState({});
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  // Memoriza los datos y reduce renders
  const initialData = useMemo(() => {
    const initialWeights = {};
    const initialCantidad = {};
    listData.forEach((item) => {
      initialWeights[item.key] = "";
      initialCantidad[item.key] = "";
    });
    return { initialWeights, initialCantidad };
  }, [listData]);

  useEffect(() => {
    setWeights(initialData.initialWeights);
    setCantidadInput(initialData.initialCantidad);
  }, [initialData]);

  const getMo = useCallback(async () => {
    setIsLoading(true);
    try {
      const fullMo = await getFullMo(
        screenName,
        typeConnect[0],
        currentScreenId
      );
      const moArray = Object.entries(fullMo).map(([po, mo]) => ({
        key: po,
        po,
        ...mo,
      }));
      dispatch(updateListaPesos(moArray));
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    getMo();
  }, [getMo]);

  const handleSearch = (searchValue) => {
    // Asegurarse de que `searchValue` es una cadena
    if (typeof searchValue !== "string") {
      console.error("searchValue no es una cadena");
      return;
    }

    // Limpiar el valor de búsqueda, reemplazar ' con - y dividir por espacios
    const moArray = searchValue
      .replace(/'/g, "-")
      .split(" ")
      .map((mo) => mo.trim());

    const uniqueResults = new Set();

    moArray.forEach((mo) => {
      // Buscar el MO en listData
      const result = listData.find((item) => item.mo === mo);
      if (result) {
        uniqueResults.add(result); // Agregar el resultado si se encuentra
      } else {
        console.log(`La MO ${mo} no está en la lista.`);
      }
    });

    // Convertir Set a Array para evitar duplicados y actualizar resultados
    const uniqueResultsArray = Array.from(uniqueResults);
    setSearchedMOs(uniqueResultsArray.map((result) => result.mo));
    setSearchResults(uniqueResultsArray);

    // Limpiar el campo de búsqueda
    setRefSearch("");
  };

  const handleAdd = useCallback(
    async (item) => {
      const newWeightValue = weights[item.key];
      const newCantidadValue = cantidadInput[item.key];

      // Convertir a números para validación y envío
      const weightNumber = parseFloat(newWeightValue);
      const cantidadNumber = parseFloat(newCantidadValue);

      if (!newWeightValue || isNaN(weightNumber) || weightNumber === 0) {
        console.log("El valor de peso es inválido o cero.");
        return;
      }

      if (!newCantidadValue || isNaN(cantidadNumber) || cantidadNumber === 0) {
        console.log("El valor de cantidad es inválido o cero.");
        return;
      }

      // Definir los límites de peso
      const limiteSuperior = item.cantidad * item.gramos * 1.1; // +10%
      const limiteInferior = cantidadNumber * item.gramos; // Peso esperado

      if (weightNumber > limiteSuperior) {
        // Peso supera el límite superior, solicitar contraseña

        const userAuthRodriguez = authQualityUsers.find(
          (user) => user.usuario === "j.rodriguez@facet.es"
        );
        const userAuthSerret = authQualityUsers.find(
          (user) => user.usuario === "jm.serret@facet.es"
        );

        let contraseña = prompt(
          "El peso excede el límite permitido. Por favor, introduce la contraseña (JM o JR) para continuar:"
        );

        if (
          (userAuthRodriguez &&
            contraseña === userAuthRodriguez.password_calidad) ||
          (userAuthSerret && contraseña === userAuthSerret.password_calidad)
        ) {
          const logData = `MO: ${
            item.mo
          }, Peso: ${weightNumber}, Contraseña Usada: ${contraseña}, Fecha y Hora: ${new Date().toLocaleString()}`;
          await setLog(SCREEN_IDS.GENERAL, "LOG", "PesajeIntermedio", logData);

          // Llamar a la función para añadir el peso (convertir a números)
          const status = await insertNewIntermediateWeight(
            item.po,
            item.mo,
            usuario,
            equipo,
            1,
            weightNumber,
            cantidadNumber,
            getCurrentDateTime(),
            "Repaso.",
            tipo_tiempo,
            currentScreenId,
            item.cantidad
          );

          if (status) {
            console.log("Peso añadido correctamente con autorización.");
            // Remueve el item añadido de la lista actual sin tener que refrescar todo
            setSearchResults((prevResults) =>
              prevResults.filter((rowData) => rowData.mo !== item.mo)
            );
            dispatch(
              updateListaPesos(
                listData.filter((rowData) => rowData.mo !== item.mo)
              )
            );
            setSearchResult(null);
            setWeights((prevWeights) => ({
              ...prevWeights,
              [item.key]: "",
            }));
            setCantidadInput((prevCantidadInput) => ({
              ...prevCantidadInput,
              [item.key]: "",
            }));
          } else {
          notify.error(
            "Error al añadir peso. Revisa la función insertNewIntermediateWeight."
          );
          }
        } else {
          notify.error("Contraseña incorrecta. No se puede continuar.");
        }
      }
      // else if (parseFloat(newWeightValue) < limiteInferior) {
      //   notify.error(
      //     "El peso está por debajo del límite esperado y no se puede continuar."
      //   );
      // }
      else {
        const status = await insertNewIntermediateWeight(
          item.po,
          item.mo,
          usuario,
          equipo,
          1,
          weightNumber,
          cantidadNumber,
          getCurrentDateTime(),
          "Repaso.",
          tipo_tiempo,
          currentScreenId,
          item.cantidad
        );

        if (status) {
          console.log("Peso añadido correctamente.");

          const logDataNormal = `MO: ${
            item.mo
          }, Peso: ${weightNumber}, Fecha y Hora: ${new Date().toLocaleString()}, Usuario: ${usuario}, Equipo: ${equipo}`;
          await setLog(
            SCREEN_IDS.GENERAL,
            "LOG",
            "PesajeIntermedioSinClave",
            logDataNormal
          );

          setSearchResults((prevResults) =>
            prevResults.filter((rowData) => rowData.mo !== item.mo)
          );
          dispatch(
            updateListaPesos(
              listData.filter((rowData) => rowData.mo !== item.mo)
            )
          );
          setSearchResult(null);
          setWeights((prevWeights) => ({
            ...prevWeights,
            [item.key]: "",
          }));
          setCantidadInput((prevCantidadInput) => ({
            ...prevCantidadInput,
            [item.key]: "",
          }));
        } else {
          notify.error(
            "Error al añadir peso. Revisa la función insertNewIntermediateWeight."
          );
        }
      }
    },
    [
      weights,
      cantidadInput,
      usuario,
      equipo,
      currentScreenId,
      authQualityUsers,
      dispatch,
      listData,
    ]
  );


  const rowsToShow = searchResults.length > 0 ? searchResults : listData;

  return (
    <Box sx={{ padding: 1.5 }}>
      <DeleteWeightButton
        currentScreenId={currentScreenId}
        onWeightDeleted={getMo}
        isResetModalOpen={isResetModalOpen}
        toggleResetModal={() => setIsResetModalOpen((prev) => !prev)}
      />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          padding: 1.5,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          marginBottom: 1.5,
        }}>
        <Box sx={{ flex: 1, minWidth: "280px", position: "relative" }}>
          <InputBase
            inputRef={scanInputRef}
            value={refSearch || ""}
            onChange={(e) => {
              let inputValue = e.target.value.replace(/'/g, "-");

              if (/^\d{6}-\d{3}$/.test(inputValue.split(" ").slice(-1)[0])) {
                inputValue += " ";
              }

              setRefSearch(inputValue);
            }}
            placeholder={translateText("Código de MO", lang)}
            sx={{
              width: "100%",
              backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_CARD || "#F9FAFB",
              color: INTERMEDIATE_WEIGHING_COLORS.TEXT_PRIMARY || "#374151",
              fontSize: "0.85rem",
              fontWeight: 500,
              padding: "8px 12px 8px 40px",
              borderRadius: 2,
              border: `1.5px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_CARD || "#e5e7eb"}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_INPUT || "#F3F4F6",
                borderColor: INTERMEDIATE_WEIGHING_COLORS.BORDER_BRAND || "#174045",
                boxShadow: "0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "&.Mui-focused": {
                backgroundColor: "white",
                borderColor: INTERMEDIATE_WEIGHING_COLORS.BORDER_BRAND || "#174045",
                boxShadow:
                  "0 0 0 2px rgba(70, 93, 100, 0.1), 0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "& input": {
                padding: 0,
              },
              "& input::placeholder": {
                color: INTERMEDIATE_WEIGHING_COLORS.TEXT_MUTED || "#9CA3AF",
                opacity: 1,
              },
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: INTERMEDIATE_WEIGHING_COLORS.TEXT_MUTED || "#9CA3AF",
              pointerEvents: "none",
            }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>

        <Button
          onClick={() => handleSearch(refSearch)}
          variant='contained'
          size="small"
          startIcon={<SearchIcon />}
          sx={{
            backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_BUTTON_PRIMARY || "#174045",
            color: "white",
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_BUTTON_SECONDARY || "#1e4d52",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          {translateText("Buscar", lang)}
        </Button>

        {/* Ventana de restricciones */}
        <Box
          sx={{
            backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_ERROR,
            color: INTERMEDIATE_WEIGHING_COLORS.TEXT_ERROR,
            padding: "6px 12px",
            borderRadius: 2,
            border: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_ERROR}`,
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            fontSize: "0.75rem",
            lineHeight: 1.3,
            "@media (max-width: 768px)": {
              display: "none",
            },
          }}>
          <Typography
            variant='body2'
            sx={{
              fontWeight: 600,
              marginBottom: "2px",
              fontSize: "0.75rem",
            }}>
            {translateText("Restricciones", lang)}:
          </Typography>
          <Typography
            variant='caption'
            sx={{
              lineHeight: 1.3,
              fontSize: "0.7rem",
            }}>
            - {translateText("El peso no puede ser 0", lang)}. <br />-{" "}
            {translateText(
              "El peso no puede superar el 10% del peso esperado",
              lang
            )}
            .
          </Typography>
        </Box>

        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: "1px",
            height: 32,
            backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BORDER_CARD || "#e5e7eb",
          }}
        />

        <Typography variant='h6' sx={{ flex: 1, textAlign: "center", fontSize: "1rem", fontWeight: 600 }}>
          {translateText("Tabla de MO", lang)}
        </Typography>

        <IconButton
          onClick={() => setIsResetModalOpen(true)}
          sx={{
            padding: 1,
            color: INTERMEDIATE_WEIGHING_COLORS.TEXT_BRAND || "#174045",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(70, 93, 100, 0.08)",
              transform: "scale(1.1)",
            },
          }}>
          <RestartAltIcon />
        </IconButton>
      </Box>
      {isLoading ? (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      translateText("PO", lang),
                      translateText("MO", lang),
                      translateText("Cantidad", lang),
                      translateText("Referencia", lang),
                      translateText("Entrega", lang),
                      translateText("Stamping", lang),
                      translateText("Gramos recorte", lang),
                      translateText("Gramos piezas", lang),
                      translateText("Registrar Peso", lang),
                    ].map((header, i) => (
                      <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                        <Skeleton variant="text" width="100%" height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(9)].map((_, j) => (
                        <TableCell key={j} sx={{ py: 0.75, px: 1.5 }}>
                          <Skeleton variant="text" width="100%" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      ) : rowsToShow.length === 0 ? (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
            textAlign: "center",
            py: 8,
          }}>
          <Typography variant='h6' sx={{ color: INTERMEDIATE_WEIGHING_COLORS.TEXT_SECONDARY || "#6B7280" }}>
            {translateText("No hay MOs pendientes...", lang)}
          </Typography>
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <TableContainer sx={{ maxHeight: "calc(78vh - 60px)" }}>
            <Table stickyHeader size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("PO", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("MO", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Cantidad", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Referencia", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Entrega", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Stamping", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Gramos recorte", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Gramos piezas", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Registrar Peso", lang)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rowsToShow
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow
                      key={item.key}
                      sx={{
                        "&:hover": {
                          backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
                        },
                        borderBottom: `1px solid ${INTERMEDIATE_WEIGHING_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
                      }}>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: INTERMEDIATE_WEIGHING_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.po}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: INTERMEDIATE_WEIGHING_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.mo}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: INTERMEDIATE_WEIGHING_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {Number(item.cantidad).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: INTERMEDIATE_WEIGHING_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.referencia}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: INTERMEDIATE_WEIGHING_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {formatDate(item.fechaentrega)}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: INTERMEDIATE_WEIGHING_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.stamping}
                      </TableCell>
                      <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                        <TextField
                          value={cantidadInput[item.key] || ""}
                          onChange={(e) => {
                            const normalized = normalizeNumericInput(e.target.value);
                            setCantidadInput((prev) => ({
                              ...prev,
                              [item.key]: normalized,
                            }));
                          }}
                          type='text'
                          inputMode='decimal'
                          placeholder={translateText("Recorte", lang)}
                          size='small'
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              backgroundColor: "white",
                              height: "28px",
                              width: "100px",
                              "& fieldset": {
                                borderColor: INTERMEDIATE_WEIGHING_COLORS.BORDER_LIGHT || "#e5e7eb",
                              },
                              "&:hover fieldset": {
                                borderColor: INTERMEDIATE_WEIGHING_COLORS.BORDER_BRAND || "#174045",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: INTERMEDIATE_WEIGHING_COLORS.BORDER_BRAND || "#174045",
                                borderWidth: "1px",
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              textAlign: "center",
                              fontSize: "0.75rem",
                              py: 0.5,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                        <TextField
                          value={weights[item.key] || ""}
                          onChange={(e) => {
                            const normalized = normalizeNumericInput(e.target.value);
                            setWeights((prev) => ({
                              ...prev,
                              [item.key]: normalized,
                            }));
                          }}
                          type='text'
                          inputMode='decimal'
                          placeholder={translateText("Peso", lang)}
                          size='small'
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              backgroundColor: "white",
                              height: "28px",
                              width: "100px",
                              "& fieldset": {
                                borderColor: INTERMEDIATE_WEIGHING_COLORS.BORDER_LIGHT || "#e5e7eb",
                              },
                              "&:hover fieldset": {
                                borderColor: INTERMEDIATE_WEIGHING_COLORS.BORDER_BRAND || "#174045",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: INTERMEDIATE_WEIGHING_COLORS.BORDER_BRAND || "#174045",
                                borderWidth: "1px",
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              textAlign: "center",
                              fontSize: "0.75rem",
                              py: 0.5,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                        <Button
                          onClick={() => handleAdd(item)}
                          variant='contained'
                          sx={{
                            width: "120px",
                            padding: "4px 12px",
                            fontSize: "0.75rem",
                            height: "28px",
                            backgroundColor: backgroundColor2,
                            textTransform: "none",
                            borderRadius: 1,
                            boxShadow: "none",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_HOVER || "#1e4d52",
                              transform: "translateY(-1px)",
                              boxShadow: "0 2px 4px rgba(70, 93, 100, 0.2)",
                            },
                          }}>
                          {translateText("Registrar", lang)}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            count={rowsToShow.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              backgroundColor: INTERMEDIATE_WEIGHING_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
              "& .MuiTablePagination-toolbar": {
                fontSize: "0.75rem",
                color: INTERMEDIATE_WEIGHING_COLORS.TEXT_SECONDARY || "#6B7280",
                minHeight: "52px",
              },
              "& .MuiTablePagination-select": {
                fontSize: "0.75rem",
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: "0.75rem",
              },
            }}
          />
        </Paper>
      )}
    </Box>
  );
}
